import * as z from 'zod'

import type { AffiliateAdminAccessGrantSchema } from '@/api/gen'
import { AffiliatePartnerRole } from '@/api/gen'

/**
 * Admin-access grants: the grant form's validation and the role vocabulary the
 * access page renders. The server is the authority — it refuses an unknown
 * account, an existing admin and the caller's own account with a 422 — so this
 * validation only catches an empty or malformed address before the round-trip.
 */

const V = {
  required: 'adminAccess.validation.required',
  email: 'adminAccess.validation.email',
} as const

export const ADMIN_ACCESS_ERROR_PREFIX = 'adminAccess.'

export const adminAccessGrantFormSchema = z.object({
  email: z.string().trim().min(1, V.required).pipe(z.email(V.email)),
})

export type AdminAccessGrantFormValues = z.infer<typeof adminAccessGrantFormSchema>

export const ADMIN_ACCESS_GRANT_DEFAULTS: AdminAccessGrantFormValues = { email: '' }

export const buildAdminAccessGrantRequest = (
  values: AdminAccessGrantFormValues,
): AffiliateAdminAccessGrantSchema => ({ email: values.email.trim() })

const ROLE_LABEL_IDS = {
  PARTNER: 'adminAccess.role.partner',
  MANAGER: 'adminAccess.role.manager',
  ACCOUNTANT: 'adminAccess.role.accountant',
  ADMIN: 'adminAccess.role.admin',
} as const satisfies Record<AffiliatePartnerRole, string>

const isPartnerRole = (value: string): value is AffiliatePartnerRole =>
  (Object.values(AffiliatePartnerRole) as string[]).includes(value)

/** Message id for a role, or `null` for a value the client does not know yet. */
export const roleLabelId = (role: string) => (isPartnerRole(role) ? ROLE_LABEL_IDS[role] : null)
